import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trpc } from 'src/trpc';
import { AddressForm } from 'components/address/AddressForm';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader } from 'src/components/Loader';
import { Check, Edit } from 'lucide-react';

type Props = {
  userId: string;
  selectedAddressId?: string;
  onAddressSelect: (addressId: string) => void;
  addressType?: 'billing' | 'delivery' | 'all';
  loading?: boolean;
};

export const AddressSelector = ({
  userId,
  selectedAddressId,
  onAddressSelect,
  addressType = 'all',
  loading = false,
}: Props) => {
  const [mode, setMode] = useState<'list' | 'new' | 'edit'>('list');
  const [isSelecting, setIsSelecting] = useState<string | null>(null);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);

  const { data: allAddresses, isLoading: isLoadingAddresses } =
    trpc.admin.address.adminListUserAddresses.useQuery(userId, {
      enabled: !!userId,
    });

  // Filter addresses based on addressType prop
  const addresses = allAddresses?.filter((addr) => {
    if (addressType === 'all') return true;
    return addr.type === addressType;
  });

  const startAddNew = () => {
    setEditingAddressId(null);
    setMode('new');
  };

  const startEdit = (addressId: string) => {
    setEditingAddressId(addressId);
    setMode('edit');
  };

  const backToList = () => {
    setMode('list');
    setEditingAddressId(null);
  };

  const handleSelect = (addressId: string) => {
    setIsSelecting(addressId);
    onAddressSelect(addressId);
  };

  // Handle successful address creation
  const handleAddressCreated = (addressId: string) => {
    // Auto-select the newly created address
    onAddressSelect(addressId);
    // Return to the address list
    backToList();
  };

  const getAddressTypeLabel = () => {
    switch (addressType) {
      case 'billing':
        return 'billing address';
      case 'delivery':
        return 'delivery address';
      case 'all':
        return 'address';
      default:
        return 'address';
    }
  };

  const getDefaultAddressType = () => {
    return addressType === 'all' ? 'billing' : addressType;
  };

  return (
    <div className="space-y-4">
      {mode === 'list' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2">
            <p className="text-sm text-slate-700">Select a {getAddressTypeLabel()}</p>
            <Button variant="outline" size="sm" onClick={startAddNew} disabled={loading}>
              Add Address
            </Button>
          </div>
          {isLoadingAddresses ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : addresses?.length ? (
            addressType === 'all' ? (
              (() => {
                // Group addresses by type when showing all
                const billingAddresses = addresses.filter((addr) => addr.type === 'billing');
                const deliveryAddresses = addresses.filter((addr) => addr.type === 'delivery');

                return (
                  <div className="space-y-4">
                    {billingAddresses.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700 px-1">
                          Billing Addresses
                        </h4>
                        {billingAddresses.map((addr) => (
                          <div
                            key={addr._id}
                            className={`w-full p-3 border rounded cursor-pointer ${
                              addr._id === selectedAddressId
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200'
                            }`}
                            onClick={() => handleSelect(addr._id!)}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    Billing
                                  </Badge>
                                </div>
                                <p className="text-sm text-text-primary truncate">{addr.line1}</p>
                                {addr.line2 && (
                                  <p className="text-xs text-slate-600 truncate">{addr.line2}</p>
                                )}
                                <p className="text-xs text-slate-600 truncate">
                                  {addr.city}, {addr.state} {addr.postalCode}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  variant="ghost"
                                  className="hover:bg-secondary"
                                  size="sm"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    startEdit(addr._id!);
                                  }}
                                  disabled={loading}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {loading &&
                                (isSelecting === addr._id || addr._id === selectedAddressId) ? (
                                  <Loader />
                                ) : addr._id === selectedAddressId ? (
                                  <div className="w-5 h-5 m-2 bg-primary rounded-full flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 p-4 hover:bg-secondary rounded flex items-center justify-center">
                                    <Checkbox disabled={loading} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {deliveryAddresses.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700 px-1">
                          Delivery Addresses
                        </h4>
                        {deliveryAddresses.map((addr) => (
                          <div
                            key={addr._id}
                            className={`w-full p-3 border rounded cursor-pointer ${
                              addr._id === selectedAddressId
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200'
                            }`}
                            onClick={() => handleSelect(addr._id!)}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="secondary" className="text-xs">
                                    Delivery
                                  </Badge>
                                </div>
                                <p className="text-sm text-text-primary truncate">{addr.line1}</p>
                                {addr.line2 && (
                                  <p className="text-xs text-slate-600 truncate">{addr.line2}</p>
                                )}
                                <p className="text-xs text-slate-600 truncate">
                                  {addr.city}, {addr.state} {addr.postalCode}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  variant="ghost"
                                  className="hover:bg-secondary"
                                  size="sm"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    startEdit(addr._id!);
                                  }}
                                  disabled={loading}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {loading &&
                                (isSelecting === addr._id || addr._id === selectedAddressId) ? (
                                  <Loader />
                                ) : addr._id === selectedAddressId ? (
                                  <div className="w-5 h-5 m-2 bg-primary rounded-full flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 p-4 hover:bg-secondary rounded flex items-center justify-center">
                                    <Checkbox disabled={loading} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              // Non-grouped display for specific address types
              addresses.map((addr) => (
                <div
                  key={addr._id}
                  className={`w-full p-3 border rounded cursor-pointer ${
                    addr._id === selectedAddressId
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200'
                  }`}
                  onClick={() => handleSelect(addr._id!)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary truncate">{addr.line1}</p>
                      {addr.line2 && (
                        <p className="text-xs text-slate-600 truncate">{addr.line2}</p>
                      )}
                      <p className="text-xs text-slate-600 truncate">
                        {addr.city}, {addr.state} {addr.postalCode}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        className="hover:bg-secondary"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          startEdit(addr._id!);
                        }}
                        disabled={loading}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {loading && (isSelecting === addr._id || addr._id === selectedAddressId) ? (
                        <Loader />
                      ) : addr._id === selectedAddressId ? (
                        <div className="w-5 h-5 m-2 hover:bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 p-4 bg-secondary rounded flex items-center justify-center">
                          <Checkbox disabled={loading} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            <div className="rounded border border-dashed p-6 text-center text-sm text-slate-600">
              No {addressType === 'all' ? '' : addressType} addresses found for this customer.
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={startAddNew}>
                  Add Address
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {mode !== 'list' && (
        <div className="space-y-4">
          {mode === 'edit' && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
              <p>
                <strong>Note:</strong> Changes made here will update the underlying customer address
                information throughout the entire system, including all invoices and orders.
              </p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {mode === 'new' ? `Add New ${getAddressTypeLabel()}` : 'Edit Address'}
            </p>
            <Button variant="ghost" size="sm" onClick={backToList}>
              Back to list
            </Button>
          </div>
          <div className="card bg-slate-50 p-4 w-full">
            <AddressForm
              addressType={
                mode === 'edit'
                  ? allAddresses?.find((a) => a._id === editingAddressId)?.type ||
                    getDefaultAddressType()
                  : getDefaultAddressType()
              }
              userId={userId}
              existingAddress={
                mode === 'edit' ? allAddresses?.find((a) => a._id === editingAddressId) : undefined
              }
              googlePlacesId={
                mode === 'edit'
                  ? allAddresses?.find((a) => a._id === editingAddressId)?.googlePlacesId
                  : undefined
              }
              onSuccess={mode === 'new' ? handleAddressCreated : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSelector;
